import {Tooltip} from '@mui/material'
import {Box, Loading, Typography} from '@viptech/react-components'
import {ApexOptions} from 'apexcharts'
import {capitalize} from 'lodash'
import {useEffect, useRef, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import Dropdown from 'src/components/dropdown/Dropdown'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'

export type ChartData = {
  name: string
  data: Array<number>
}

type Props = {
  title: string
  loading: boolean
  data: Array<ChartData>
  labelsXaxis: Array<string | number | Array<string>> | Array<Date> | undefined
  height?: string
  refetching?: boolean
  subtitle?: string
  setType: (type: string) => void
}

const GAP = 52

function DashboardHeatMapTable({title, loading, refetching, data, height = '420px', labelsXaxis, subtitle, setType}: Props) {
  const {styledTheme} = usePersonalizedThemeByCompany()
  const containerRef = useRef<HTMLDivElement>(null)
  const [type, setTypeChart] = useState({id: 'total', label: 'Total'})

  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - GAP)
      }
    }

    updateContainerWidth()

    window.addEventListener('resize', updateContainerWidth)
    const observer = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth - GAP)
    }
  }, [loading, refetching])

  const maxValue = Math.max(...data.flatMap((item) => item.data).filter((value): value is number => value !== null))
  const minValue = Math.min(...data.flatMap((item) => item.data).filter((value): value is number => value !== null))
  const colorsToLegend = [
    styledTheme.dashboard.heatMapColorRanges.lowest,
    styledTheme.dashboard.heatMapColorRanges.lower,
    styledTheme.dashboard.heatMapColorRanges.low,
    styledTheme.dashboard.heatMapColorRanges.lowMedium,
    styledTheme.dashboard.heatMapColorRanges.lowerMedium,
    styledTheme.dashboard.heatMapColorRanges.medium,
    styledTheme.dashboard.heatMapColorRanges.high,
    styledTheme.dashboard.heatMapColorRanges.extreme,
  ]

  const numberOfRanges = colorsToLegend.length
  const step = (maxValue - minValue) / numberOfRanges

  const ranges = colorsToLegend.map((color, index) => {
    const from = minValue + index * step
    const to = index === numberOfRanges - 1 ? maxValue : minValue + (index + 1) * step - 1

    return {
      from: Math.floor(from),
      to: Math.floor(to),
      color,
    }
  })

  const today = capitalize(new Date().toLocaleString('pt-br', {weekday: 'short'}).replace('.', ''))

  const options: ApexOptions = {
    series: data,
    chart: {
      type: 'heatmap',
      fontFamily: "'Inter', 'sans-serif",
      toolbar: {
        show: false,
      },
    },
    yaxis: {
      show: true,
      labels: {
        style: {
          colors: data.map((dia) => (dia.name === today ? '#000' : '#505253')),
          fontWeight: 400,
        },
      },
    },
    xaxis: {
      categories: labelsXaxis ?? [],
      labels: {
        trim: true,
        hideOverlappingLabels: true,
        rotate: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.3,
        radius: 0,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: ranges,
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
    },
    tooltip: {
      enabled: true,
      y: {
        title: {
          formatter: (val) => val,
        },
        formatter(val) {
          return val + ' visitantes'
        },
      },
    },
  }

  return (
    <Box
      ref={containerRef}
      position='relative'
      backgroundColor='white'
      borderRadius='16px'
      width='100%'
      height={height}
      style={{boxShadow: '0px 5px 8px 0px #00000026'}}>
      <Box display='flex' direction='column' padding='24px' rowGap='6px' align='center' flex={1}>
        <Box display='flex' width='100%' align='center' justifyContent='center' position='relative'>
          <Box display='flex' direction='column' align='center' justify='center' rowGap='6px' position='relative'>
            <Typography variant='span' size='16px' color={styledTheme.dashboard.colors.title} children={title} weight={600} />
            <Typography
              variant='span'
              size='12px'
              color={styledTheme.dashboard.colors.title}
              style={{fontStyle: 'italic'}}
              children={subtitle?.replace('.', '')}
            />
          </Box>

          <Box position='absolute' right='20px'>
            <Dropdown
              labelFontSize='12px'
              borderColor='#D0D5DD'
              fontSize='12px'
              height='32px'
              width='135px'
              placeholder=''
              onChangeSelected={(e) => {
                setType(e.id)
                setTypeChart(e)
              }}
              selected={type}
              items={[
                {id: 'total', label: 'Total'},
                {id: 'average', label: 'Média'},
              ]}
            />
          </Box>
        </Box>

        {(refetching || loading) && (
          <Tooltip title='Buscando atualizações' placement='top'>
            <Box position='absolute' right='24px' display='flex' columnGap='8px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        <Box display='flex' direction='column' align='center'>
          <ReactApexChart options={options} series={options.series} type='heatmap' height={300} width={containerWidth} />
          <Box display='flex' columnGap='10px' align='center'>
            <Typography variant='label' size='12px' color='#55565B' weight={400} children={`Menos pessoas: ${minValue}`} />

            <Box display='flex'>
              {ranges.map((it) => (
                <Box backgroundColor={it.color} width='52px' height='8px'></Box>
              ))}
            </Box>

            <Typography variant='label' size='12px' color='#55565B' weight={400} children={`Mais pessoas: ${maxValue}`} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default DashboardHeatMapTable
